import React, { FC, RefObject } from "react";
import { darkTheme } from "@yleisradio/yds-core";
import {
  Close,
  MoreVertical,
  Settings,
  SubtitlesMultiaudio
} from "@yleisradio/yds-icons-react";
import styled from "styled-components";

import { useEmbedParametersContext } from "../../contexts/EmbedParametersContext";
import i18n, { TranslationKey } from "../../service/i18n";
import { usePlayerStore } from "../../store/usePlayerStore";
import { focusColor } from "../../styles/variables";
import { SettingsName } from "../../types/common";
import { ControlBarButton } from "../player-ui/ControlBarButton";

const SettingsButtonStyled = styled(ControlBarButton)<{
  isMobile: boolean;
  settingsVisible: boolean;
}>`
  background-color: ${({ settingsVisible }) =>
    settingsVisible ? darkTheme.BACKGROUND_VARIANT : "transparent"};
  border-radius: ${({ isMobile, settingsVisible }) =>
    settingsVisible && !isMobile ? "0 0 8px 8px" : 0};

  &:before {
    border: 1px solid transparent;
    content: "";
    display: block;
    height: 100%;
    left: -1px;
    position: absolute;
    top: -1px;
    width: 100%;
  }

  &:focus-visible {
    outline: none !important;

    &:before {
      border-color: ${focusColor};
    }
  }
`;

const ButtonIcon = ({
  isMobile,
  isSettingsVisible,
  settingsName
}: {
  isMobile: boolean;
  isSettingsVisible: boolean;
  settingsName: SettingsName;
}) => {
  if (isMobile && isSettingsVisible) {
    return <Close />;
  }

  switch (settingsName) {
    case "desktop-settings":
      return <Settings />;
    case "desktop-texts":
      return <SubtitlesMultiaudio />;
    case "mobile":
      return <MoreVertical />;
    default:
      return null;
  }
};

type Props = {
  icon: string;
  settingsName: SettingsName;
  ref: RefObject<HTMLButtonElement | null>;
};

export const SettingsButton: FC<Props> = ({ ref, settingsName }) => {
  const { useStoreState } = usePlayerStore();
  const device = useStoreState.device();
  const visibleSettingsMenu = useStoreState.visibleSettingsMenu();
  const setVisibleSettingsMenu = useStoreState.setVisibleSettingsMenu();

  const { language } = useEmbedParametersContext();

  const closeText =
    visibleSettingsMenu && visibleSettingsMenu === settingsName ? "sulje_" : "";
  const tooltip = i18n(
    (closeText +
      (settingsName === "desktop-texts"
        ? "aani_tekstitykset"
        : "asetukset")) as TranslationKey,
    { language }
  );

  return (
    <SettingsButtonStyled
      aria-expanded={visibleSettingsMenu === settingsName}
      aria-haspopup="menu"
      aria-label={tooltip}
      isMobile={device.isMobile}
      onClick={event => {
        event.stopPropagation();
        setVisibleSettingsMenu(
          visibleSettingsMenu !== settingsName ? settingsName : undefined
        );
      }}
      ref={ref}
      settingsVisible={visibleSettingsMenu === settingsName}
      tooltip={tooltip}>
      <ButtonIcon
        isMobile={device.isMobile}
        isSettingsVisible={visibleSettingsMenu === settingsName}
        settingsName={settingsName}
      />
    </SettingsButtonStyled>
  );
};
